// Selectori: transformă tabelele brute în ce afișează interfața.
import type { Work, Copy, Reading, Member, Loan, ReadingStatus } from '../../shared/types';
import { DESTINATION_LABELS } from '../../shared/types';
import { fold, normalizeKey, parseQuery } from '../../shared/text';
import type { Tables } from '../sync/engine';

export interface BookView {
  work: Work;
  copies: Copy[];          // exemplarele vizibile în perspectiva curentă
  allCopies: Copy[];
  myReading: Reading | null;
  readers: Reading[];      // lecturile tuturor membrilor
  owners: Member[];
  activeLoans: Loan[];
}

export type Scope = 'mine' | 'family';

export interface Filters {
  query: string;
  scope: Scope;
  shelf: string | null;
  destination: string | null;
  category: string | null;
  reading: ReadingStatus | null;
  owner: string | null;
  coverReview?: boolean;
  sort: 'added_desc' | 'title' | 'author' | 'rating';
}

export const defaultFilters = (): Filters => ({
  query: '', scope: 'mine', shelf: null, destination: null, category: null, reading: null, owner: null, sort: 'added_desc'
});

export function isMyCopy(c: Copy, me: Member | null) {
  if (!me) return false;
  return c.owner_id === me.id || (!!me.household_id && c.household_id === me.household_id);
}

export function buildBooks(t: Tables, me: Member | null): BookView[] {
  const copiesByWork = new Map<string, Copy[]>();
  for (const c of t.copies.values()) {
    const arr = copiesByWork.get(c.work_id) || [];
    arr.push(c); copiesByWork.set(c.work_id, arr);
  }
  const readingByWork = new Map<string, Reading[]>();
  for (const r of t.reading.values()) {
    const arr = readingByWork.get(r.work_id) || [];
    arr.push(r); readingByWork.set(r.work_id, arr);
  }
  const loansByCopy = new Map<string, Loan[]>();
  for (const l of t.loans.values()) {
    if (l.status !== 'aprobat' && l.status !== 'cerut') continue;
    const arr = loansByCopy.get(l.copy_id) || [];
    arr.push(l); loansByCopy.set(l.copy_id, arr);
  }
  const out: BookView[] = [];
  for (const w of t.works.values()) {
    const all = copiesByWork.get(w.id) || [];
    const readers = readingByWork.get(w.id) || [];
    const owners = [...new Set(all.map(c => c.owner_id).filter(Boolean))].map(id => t.members.get(id!)).filter(Boolean) as Member[];
    out.push({
      work: w,
      copies: all,
      allCopies: all,
      myReading: readers.find(r => r.member_id === me?.id) || null,
      readers,
      owners,
      activeLoans: all.flatMap(c => loansByCopy.get(c.id) || [])
    });
  }
  return out;
}

function addedTs(b: BookView) {
  return Math.max(0, ...b.copies.map(c => Date.parse(c.added_at || c.created_at || '') || 0), Date.parse(b.work.created_at || '') || 0);
}

export function filterBooks(books: BookView[], f: Filters, me: Member | null): BookView[] {
  const q = parseQuery(f.query);
  const res: BookView[] = [];
  for (const b0 of books) {
    let copies = f.scope === 'mine' ? b0.allCopies.filter(c => isMyCopy(c, me)) : b0.allCopies;
    // o carte fără exemplare (ex. doar în „vreau s-o citesc”) apare doar în perspectiva familiei
    if (f.scope === 'mine' && copies.length === 0) continue;
    if (f.shelf) copies = copies.filter(c => (c.shelf || '') === f.shelf);
    if (f.destination) copies = copies.filter(c => c.destination === f.destination);
    if (f.owner) copies = copies.filter(c => c.owner_id === f.owner);
    if (q.locations.length) copies = copies.filter(c => q.locations.every(l => normalizeKey(`${c.shelf || ''} ${c.box || ''}`).includes(l)));
    if ((f.shelf || f.destination || f.owner || q.locations.length) && copies.length === 0) continue;

    const w = b0.work;
    if (f.coverReview && w.cover_source !== 'auto') continue;
    if (f.category && w.category !== f.category) continue;
    if (f.reading) {
      const st = b0.myReading?.status || 'necitit';
      if (st !== f.reading) continue;
    }
    if (q.tags.length) {
      const hay = normalizeKey([w.category, w.tags, ...copies.map(c => `${c.destination} ${DESTINATION_LABELS[c.destination] || ''} ${c.age_group || ''}`)].join(' '));
      if (!q.tags.every(tg => hay.includes(tg))) continue;
    }
    if (q.text.length) {
      const hay = fold(`${w.title} ${w.authors} ${w.isbn || ''} ${w.publisher || ''} ${w.category || ''}`);
      if (!q.text.every(tok => hay.includes(tok))) continue;
    }
    res.push({ ...b0, copies });
  }
  const by = (a: string, b: string) => a.localeCompare(b, 'ro');
  switch (f.sort) {
    case 'title': res.sort((a, b) => by(a.work.title, b.work.title)); break;
    case 'author': res.sort((a, b) => by(a.work.authors, b.work.authors) || by(a.work.title, b.work.title)); break;
    case 'rating': res.sort((a, b) => (b.myReading?.rating || 0) - (a.myReading?.rating || 0)); break;
    default: res.sort((a, b) => addedTs(b) - addedTs(a));
  }
  return res;
}

export function shelvesOf(books: BookView[]): Array<{ name: string; count: number }> {
  const m = new Map<string, number>();
  for (const b of books) for (const c of b.copies) { const s = (c.shelf || '').trim(); if (s) m.set(s, (m.get(s) || 0) + 1); }
  return [...m.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
}

// „Ai deja cartea asta?” — după ISBN sau titlu+autor
export function findExistingWork(t: Tables, title: string, authors: string, isbn?: string): Work | null {
  const clean = (isbn || '').replace(/[^0-9X]/gi, '');
  for (const w of t.works.values()) {
    if (clean && (w.isbn || '').replace(/[^0-9X]/gi, '') === clean) return w;
  }
  const key = normalizeKey(title) + '|' + normalizeKey(authors);
  for (const w of t.works.values()) {
    if (normalizeKey(w.title) + '|' + normalizeKey(w.authors) === key) return w;
  }
  return null;
}
